/**
 * SanPlumb — Generador del baño desde el Hunter (CLAUDE.md §XI)
 *
 * Toma el `CountsState` (lo que el usuario declaró en el wizard) y una
 * tipología §XI, y produce un `SystemData` con:
 *   - El recinto (room) según la tipología.
 *   - Los `Sanitary` posicionados a lo largo del muro trasero (z pequeño),
 *     espaciados uniformemente.
 *
 * El routing de tuberías y accesorios NO se calcula aquí; lo añade
 * `applyStrategy()` en `engine/three/sceneBuilder.ts`. Esta función solo
 * resuelve el LAYOUT físico del baño.
 *
 * Reemplaza al DEMO_SYSTEM como fuente del visor desde v1.11 — el visor
 * deja de mostrar un baño fijo cuando el usuario declara distintos counts.
 */

import type { SystemData, Sanitary, SanitaryType } from '../types';
import type { CountsState, RoomDims } from '../types/ui';
import { tipologiaByCode, type TipologiaCode } from '../constants/tipologias';
import { ugFor } from '../constants/aparatosUI';
import { DIMENSIONES_APARATOS } from '../constants/paeba';

// ──────────────────────────────────────────────────────────────────
// Mapping id Hunter → SanitaryType (+ subtype + has_ac)
// ──────────────────────────────────────────────────────────────────

interface SanitaryTemplate {
  type: SanitaryType;
  subtype: string;
  brand: string;
  has_ac: boolean;
  /** Dimensiones físicas del aparato §IX en metros (w × d × h). */
  w: number;
  d: number;
  h: number;
}

const DIM_INO = DIMENSIONES_APARATOS.Inodoro!;
const DIM_LAV = DIMENSIONES_APARATOS.Lavatorio!;
const DIM_DUC = DIMENSIONES_APARATOS.Ducha!;

/**
 * Mapa de ids del catálogo Hunter (aparatosUI.ts) a `SanitaryTemplate`.
 * Los aparatos que NO tienen geometría 3D propia (bidet) se omiten.
 */
const TEMPLATES: Record<string, SanitaryTemplate | undefined> = {
  inodoro_tanque: {
    type: 'Inodoro',
    subtype: 'con tanque',
    brand: 'TREBOL Avanti II',
    has_ac: false,
    w: DIM_INO.largo,
    d: DIM_INO.ancho,
    h: DIM_INO.altura,
  },
  inodoro_red: {
    type: 'Inodoro',
    subtype: 'tanque descarga reducida',
    brand: 'TREBOL Sentino',
    has_ac: false,
    w: DIM_INO.largo,
    d: DIM_INO.ancho,
    h: DIM_INO.altura,
  },
  inodoro_valv: {
    type: 'Inodoro',
    subtype: 'válvula semiautomática (fluxómetro)',
    brand: 'TREBOL Top Piece',
    has_ac: false,
    w: DIM_INO.largo,
    d: DIM_INO.ancho,
    h: DIM_INO.altura,
  },
  lavatorio: {
    type: 'Lavatorio',
    subtype: 'empotrado',
    brand: 'TREBOL Fontana',
    has_ac: true,
    w: DIM_LAV.largo,
    d: DIM_LAV.ancho,
    h: DIM_LAV.altura,
  },
  ducha: {
    type: 'Ducha',
    subtype: 'con mezcladora bronce',
    brand: 'VAINSA / Mezcladora bronce',
    has_ac: true,
    w: DIM_DUC.largo,
    d: DIM_DUC.ancho,
    h: 2.1,
  },
  tina: {
    type: 'Ducha',
    subtype: 'tina / bañera',
    brand: 'TREBOL Avanti tina',
    has_ac: true,
    w: 1.5,
    d: 0.75,
    h: 0.6,
  },
  lavadero: {
    type: 'Lavadero de ropa',
    subtype: 'prefabricado',
    brand: 'TREBOL Lavadero',
    has_ac: true,
    w: 0.6,
    d: 0.5,
    h: 1.0,
  },
  urinario: {
    // El urinario va MONTADO EN PARED, no apoyado en el piso. La altura
    // física del aparato es ~0.50 m, pero su borde inferior queda a +0.60
    // del NPT (su pos.y se ajusta en generateBathSystem). El eje AF está
    // a +1.28 m PAEBA §VI.
    type: 'Urinario',
    subtype: 'con tanque',
    brand: 'TREBOL Urinario',
    has_ac: false,
    w: 0.36,
    d: 0.30,
    h: 0.55,
  },
  bidet: {
    // Bidet residencial típico peruano: apoyado en el piso, AF + AC al
    // eje (+0.55 m PAEBA §VI igual que lavatorio).
    type: 'Lavatorio', // bidet usa el geom slot 'Lavatorio' del visor
    subtype: 'bidet',
    brand: 'TREBOL Bidet',
    has_ac: true,
    w: 0.40,
    d: 0.55,
    h: 0.40,
  },
};

/** Aparatos que van MONTADOS en la pared (no apoyados en el piso). */
const APARATO_MURADO_OFFSET: Record<string, number | undefined> = {
  urinario: 0.6, // borde inferior del urinario a +60 cm del NPT
};

/**
 * Separación antropométrica MÍNIMA libre entre el borde del aparato y el
 * borde del aparato vecino (espacio libre entre bordes, no centro a centro).
 * Valores típicos peruanos / RNE para baños residenciales — antes del
 * placement se valida que cada aparato tenga al menos esta separación a
 * cada lado (en X). Si no cabe, log warning explícito.
 *
 *   inodoro:    0.30 m libre a cada lado del borde
 *   lavatorio:  0.20 m
 *   urinario:   0.30 m
 *   ducha/tina: 0.10 m (suelen ir en esquina)
 *   bidet:      0.20 m
 *   lavadero:   0.20 m
 */
const SEPARACION_MIN_LIBRE: Record<string, number> = {
  inodoro_tanque: 0.3,
  inodoro_red: 0.3,
  inodoro_valv: 0.3,
  urinario: 0.3,
  lavatorio: 0.2,
  bidet: 0.2,
  lavadero: 0.2,
  ducha: 0.16,
  tina: 0.16,
};

function isLavaderoEntry(uiId: string): boolean {
  return uiId === 'lavadero' || uiId === 'lavadero_platos';
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

/**
 * Calcula la posición esquinera de un aparato (ducha o tina). El aparato se
 * orienta con su lado largo a lo largo del muro más largo del recinto. La
 * grifería queda contra el muro corto sobre el que se apoya.
 *
 *   - `slot = 'TR'`: esquina trasera-derecha (z mínimo, x máximo). Default
 *     para ducha o tina cuando solo hay uno de los dos.
 *   - `slot = 'TL'`: esquina trasera-izquierda (z mínimo, x mínimo). Usada
 *     cuando ya hay otro aparato esquinero en TR. Se desplaza un poco hacia
 *     el interior para no chocar con el ensamblaje de llaves de paso.
 *
 * Devuelve la posición CENTRADA del aparato (estándar de Sanitary) y las
 * dimensiones reales orientadas según la esquina.
 */
function computeCornerPosition(
  slot: 'TR' | 'TL',
  tpl: SanitaryTemplate,
  dims: RoomDims,
  uiId?: string,
): { x: number; z: number; w: number; d: number } {
  const long = Math.max(tpl.w, tpl.d);
  const short = Math.min(tpl.w, tpl.d);
  const margin = 0.05;
  // TINA SIEMPRE rotada (v1.20 — feedback del usuario): largo en Z,
  // espalda contra el muro lateral X=dims.w. Sus tubos AF/AC salen por
  // el muro lateral en su cabecera. La regla aplica a tina, no a ducha.
  const isTina = uiId === 'tina';
  if (slot === 'TR') {
    if (isTina) {
      return {
        x: dims.w - short / 2 - margin,
        z: long / 2 + margin,
        w: short,
        d: long,
      };
    }
    // Ducha estándar y otros aparatos esquineros: largo a lo largo del
    // muro trasero (siempre que quepa). Si no cabe, rota a lateral.
    const lpReserve = 0.55;
    const fitsHorizontal = dims.w - lpReserve >= long + margin;
    if (fitsHorizontal) {
      return {
        x: dims.w - long / 2 - margin,
        z: short / 2 + margin,
        w: long,
        d: short,
      };
    }
    return {
      x: dims.w - short / 2 - margin,
      z: long / 2 + margin,
      w: short,
      d: long,
    };
  }
  // slot === 'TL': pegado al muro X=0 con el lado largo rotado 90°
  // (proyectado en Z, hacia el frente del baño). Pega al muro corto Z=0.
  return {
    x: short / 2 + margin,
    z: long / 2 + margin,
    w: short,
    d: long,
  };
}

// ──────────────────────────────────────────────────────────────────
// generateBathSystem
// ──────────────────────────────────────────────────────────────────

/**
 * Construye un SystemData (room + sanitarios SIN tuberías) a partir del
 * `counts` del Hunter, una tipología §XI y opcionalmente dimensiones del
 * usuario.
 *
 * Reglas de dimensionado del recinto:
 *  - Si `roomDims` está presente (el usuario declaró medidas en la
 *    ingesta) se usan ESAS dimensiones. Los muros virtuales se construyen
 *    contra los valores reales del baño del usuario.
 *  - Si no, se usan las dimensiones sugeridas de la tipología §XI como
 *    valores por defecto.
 *
 * Layout: todos los aparatos se alinean contra el muro trasero (z pequeño)
 * y se distribuyen uniformemente en X (válido para A1, A2, B1, C1, SH y
 * ACC). Para B2/C2 (con tina) y A3 (ducha esquina) un layout más
 * sofisticado queda como mejora futura.
 *
 * El routing (pipes + accesorios) NO se genera aquí — eso lo hace
 * `applyStrategy()` cuando el visor llama a `systemForRouting`.
 */
export function generateBathSystem(
  counts: CountsState,
  tipologiaCode: TipologiaCode,
  mode: 'priv' | 'pub' = 'priv',
  roomDims?: RoomDims,
): SystemData {
  const tipo = tipologiaByCode(tipologiaCode);
  // Las medidas reales del recinto las da el usuario en la ingesta. La
  // tabla §XI solo es el fallback cuando no hay declaración explícita.
  const dims: RoomDims = roomDims ?? { ...tipo.room, murW: 0.15, contrapiso: 0.05 };

  // Orden de placement de los aparatos a lo largo del muro trasero.
  // Los aparatos has_ac=true (que sí reciben agua caliente) van PRIMERO
  // (cerca del ensamblaje de llaves de paso) y los que NO tienen AC
  // (inodoros, urinario) van AL FINAL, en el extremo opuesto.
  //
  // Por qué: el main AC arranca del LP (izquierda) y termina en el último
  // aparato con AC. Si los inodoros van al final del recorrido, el main
  // AC nunca cruza por encima de ellos — coherente con CLAUDE.md (los
  // inodoros no llevan AC).
  const APARATOS_ORDER = [
    'lavatorio',
    'ducha',
    'tina',
    'lavadero',
    'inodoro_tanque',
    'inodoro_red',
    'inodoro_valv',
    'urinario',
  ];

  const sanitaries: Sanitary[] = [];
  const z_back = 0.3; // distancia del frente del aparato al muro trasero (z=0)

  // Clasificar aparatos: los esquineros (ducha, tina) van a esquinas
  // dedicadas; los demás se distribuyen en el muro trasero. Regla del
  // diseño aprobado: duchas y tinas NUNCA en medio del espacio.
  const cornerEntries: { id: string; tpl: SanitaryTemplate; uiId: string }[] = [];
  const wallEntries: { id: string; tpl: SanitaryTemplate; uiId: string }[] = [];
  for (const aparatoId of APARATOS_ORDER) {
    const c = counts[aparatoId] ?? 0;
    const tpl = TEMPLATES[aparatoId];
    if (!tpl || c <= 0) continue;
    for (let i = 0; i < c; i++) {
      const entry = { id: `s_${aparatoId}_${i}`, tpl, uiId: aparatoId };
      if (aparatoId === 'ducha' || aparatoId === 'tina') {
        cornerEntries.push(entry);
      } else {
        wallEntries.push(entry);
      }
    }
  }

  // Solo soportamos hasta 2 esquineros (TR + TL). Si hay más (caso no típico),
  // los excedentes se tratan como aparatos de muro normales.
  const cornerSlots: ('TR' | 'TL')[] = ['TR', 'TL'];
  const overflowCorners = cornerEntries.splice(cornerSlots.length);
  wallEntries.unshift(...overflowCorners);
  wallEntries.sort((a, b) => Number(isLavaderoEntry(a.uiId)) - Number(isLavaderoEntry(b.uiId)));

  if (cornerEntries.length === 0 && wallEntries.length === 0) {
    return buildEmptySystem(dims);
  }

  // Calcular las zonas X excluidas por los aparatos esquineros para no
  // chocar con ellos al distribuir los demás en el muro trasero.
  const cornerPlacements: {
    entry: { id: string; tpl: SanitaryTemplate; uiId: string };
    slot: 'TR' | 'TL';
    pos: { x: number; z: number; w: number; d: number };
  }[] = cornerEntries.map((entry, i) => ({
    entry,
    slot: cornerSlots[i],
    pos: computeCornerPosition(cornerSlots[i], entry.tpl, dims, entry.uiId),
  }));

  const hasCornerRight = cornerPlacements.some((c) => c.slot === 'TR');
  const shouldUseLateralLavadero =
    wallEntries.some((entry) => isLavaderoEntry(entry.uiId)) && (hasCornerRight || dims.w < 2.1);
  const lateralEntries = shouldUseLateralLavadero
    ? wallEntries.filter((entry) => isLavaderoEntry(entry.uiId))
    : [];
  const backWallEntries = shouldUseLateralLavadero
    ? wallEntries.filter((entry) => !isLavaderoEntry(entry.uiId))
    : wallEntries;

  // Rango disponible para aparatos de muro: empieza a la derecha del LP y
  // del aparato TL (si existe); termina a la izquierda del TR (si existe).
  // El LP ocupa ~0.44 m centrado en x≈0.23, así que x_left ≥ 0.55 por
  // defecto (margen para que el codo de salida del LP no choque).
  const placeMargin = 0.16;
  const lpRightEdge = 0.55;
  const trBlock = cornerPlacements.find((c) => c.slot === 'TR');
  const tlBlock = cornerPlacements.find((c) => c.slot === 'TL');
  const hasLavaderoWall = backWallEntries.some((entry) => isLavaderoEntry(entry.uiId));
  const rightWallMargin = hasLavaderoWall ? 0.06 : 0.25;
  const x_left = tlBlock
    ? Math.max(lpRightEdge, tlBlock.pos.x + tlBlock.pos.w / 2 + placeMargin)
    : lpRightEdge;
  const x_right = trBlock
    ? trBlock.pos.x - trBlock.pos.w / 2 - placeMargin
    : dims.w - rightWallMargin;

  // Cálculo del ancho disponible y necesario CON separaciones antropométricas
  // mínimas entre cada par de aparatos consecutivos (v1.20). La separación
  // entre los bordes de dos aparatos vecinos es el MÁXIMO de sus mínimos.
  const span = Math.max(0, x_right - x_left);
  const totalWallW = backWallEntries.reduce((s, e) => s + e.tpl.w, 0);
  // Mínimo de separación libre entre cada par consecutivo: max(min_a, min_b)
  const minGaps: number[] = [];
  for (let i = 1; i < backWallEntries.length; i++) {
    const a = SEPARACION_MIN_LIBRE[backWallEntries[i - 1].uiId] ?? 0.1;
    const b = SEPARACION_MIN_LIBRE[backWallEntries[i].uiId] ?? 0.1;
    minGaps.push(Math.max(a, b));
  }
  const totalMinGap = minGaps.reduce((s, g) => s + g, 0);
  const needed = totalWallW + totalMinGap;
  if (backWallEntries.length > 0 && span < needed) {
    console.warn(
      `[generateBath] Separación antropométrica insuficiente en muro trasero ` +
        `(${span.toFixed(2)} m disponibles, ${needed.toFixed(2)} m necesarios ` +
        `con separación mínima entre aparatos) en tipología ${tipologiaCode}. ` +
        `Declara dims mayores en ingesta para evitar sanitarios apretados.`,
    );
  }

  // Distribución: respeta los anchos reales y aplica separación mínima
  // si HAY espacio. Si no cabe (span < needed), comprimimos uniformemente
  // — validateNoOverlap reporta el choque + el warning de arriba ya alerta.
  const gaps = backWallEntries.length - 1;
  const availableGap = span - totalWallW;
  const fitsMin = availableGap >= totalMinGap;
  let cursor = x_left;
  backWallEntries.forEach((p, idx) => {
    const tpl = p.tpl;
    const isLastLavadero = isLavaderoEntry(p.uiId) && idx === wallEntries.length - 1;
    const x = isLastLavadero
      ? Math.max(cursor + tpl.w / 2, x_right - tpl.w / 2)
      : cursor + tpl.w / 2;
    if (idx < gaps) {
      let gap: number;
      if (fitsMin) {
        // Reparte el "extra" uniformemente sobre las separaciones mínimas
        const extra = (availableGap - totalMinGap) / Math.max(1, gaps);
        gap = minGaps[idx] + Math.max(0, extra);
      } else {
        // Comprime: gap proporcional al espacio disponible, mínimo 0
        gap = Math.max(0, availableGap / Math.max(1, gaps));
      }
      cursor += tpl.w + gap;
    } else {
      cursor += tpl.w;
    }
    const { ugAF, ugAC } = ugFor(p.uiId, mode);
    const yBase = APARATO_MURADO_OFFSET[p.uiId] ?? 0;

    const san: Sanitary = {
      id: p.id,
      type: tpl.type,
      subtype: tpl.subtype,
      pos: [x, yBase, z_back],
      w: tpl.w,
      d: tpl.d,
      h: tpl.h,
      conn_af: '1/2"',
      has_ac: tpl.has_ac,
      brand: tpl.brand,
      ug_af: ugAF,
    };
    if (tpl.has_ac) {
      san.conn_ac = '1/2"';
      san.ug_ac = ugAC;
    }
    sanitaries.push(san);
  });

  lateralEntries.forEach((p, idx) => {
    const tpl = p.tpl;
    const minZ = tpl.d / 2 + 0.06;
    const maxZ = dims.d - tpl.d / 2 - 0.06;
    const rightCorner = cornerPlacements.find((c) => c.slot === 'TR');
    const afterCornerZ = rightCorner
      ? rightCorner.pos.z + rightCorner.pos.d / 2 + tpl.d / 2 + 0.12
      : maxZ;
    const z = clamp(afterCornerZ + idx * (tpl.d + 0.12), minZ, maxZ);
    const x = dims.w - tpl.w / 2 - 0.05;
    const { ugAF, ugAC } = ugFor(p.uiId, mode);
    const yBase = APARATO_MURADO_OFFSET[p.uiId] ?? 0;

    const san: Sanitary = {
      id: p.id,
      type: tpl.type,
      subtype: `${tpl.subtype} lateral`,
      pos: [x, yBase, z],
      w: tpl.w,
      d: tpl.d,
      h: tpl.h,
      conn_af: '1/2"',
      has_ac: tpl.has_ac,
      brand: tpl.brand,
      ug_af: ugAF,
    };
    if (tpl.has_ac) {
      san.conn_ac = '1/2"';
      san.ug_ac = ugAC;
    }
    sanitaries.push(san);
  });

  // Aparatos esquineros (ducha/tina). Sus dims se reorientan según la
  // esquina (largo a lo largo del muro más largo).
  for (const cp of cornerPlacements) {
    const tpl = cp.entry.tpl;
    const { ugAF, ugAC } = ugFor(cp.entry.uiId, mode);
    const san: Sanitary = {
      id: cp.entry.id,
      type: tpl.type,
      subtype: tpl.subtype,
      pos: [cp.pos.x, 0, cp.pos.z],
      w: cp.pos.w,
      d: cp.pos.d,
      h: tpl.h,
      conn_af: '1/2"',
      has_ac: tpl.has_ac,
      brand: tpl.brand,
      ug_af: ugAF,
    };
    if (tpl.has_ac) {
      san.conn_ac = '1/2"';
      san.ug_ac = ugAC;
    }
    sanitaries.push(san);
  }

  // INVARIANTE: ningún sanitario puede solaparse con otro en planta.
  // Si dos bounding boxes (x±w/2, z±d/2) se intersectan, log warning. El
  // diseño del baño peruano declarado por el usuario debería garantizarlo,
  // pero validamos en runtime por seguridad.
  validateNoOverlap(sanitaries);

  return {
    routingStrategy: 'muro_dual',
    routingSource: 'auto_generated',
    room: {
      w: dims.w,
      d: dims.d,
      h: dims.h,
      doorW: 0.9,
      doorH: 2.1,
      murW: dims.murW,
      contrapiso: dims.contrapiso,
    },
    pipes: [],
    pipes_ac: [],
    accessories: [],
    accessories_ac: [],
    sanitaries,
  };
}

function buildEmptySystem(dims: RoomDims): SystemData {
  return {
    routingStrategy: 'muro_dual',
    routingSource: 'auto_generated',
    room: {
      w: dims.w,
      d: dims.d,
      h: dims.h,
      doorW: 0.9,
      doorH: 2.1,
      murW: dims.murW,
      contrapiso: dims.contrapiso,
    },
    pipes: [],
    pipes_ac: [],
    accessories: [],
    accessories_ac: [],
    sanitaries: [],
  };
}

/**
 * Verifica que ningún sanitario solape con otro en planta. Dos bounding
 * boxes (x±w/2, z±d/2) se intersectan si AABB overlap. Útil para detectar
 * tipologías o dimensiones del usuario que dejan los aparatos sin espacio.
 *
 * NO falla — solo emite `console.warn` con los ids de los aparatos que
 * chocan. Los tests del placement verifican el invariante.
 */
export function validateNoOverlap(sanitaries: readonly Sanitary[]): readonly [string, string][] {
  const collisions: [string, string][] = [];
  for (let i = 0; i < sanitaries.length; i++) {
    const a = sanitaries[i];
    const ax = a.pos[0];
    const az = a.pos[2];
    for (let j = i + 1; j < sanitaries.length; j++) {
      const b = sanitaries[j];
      const bx = b.pos[0];
      const bz = b.pos[2];
      const overlapX = Math.abs(ax - bx) < (a.w + b.w) / 2;
      const overlapZ = Math.abs(az - bz) < (a.d + b.d) / 2;
      if (overlapX && overlapZ) {
        collisions.push([a.id, b.id]);
      }
    }
  }
  if (collisions.length > 0) {
    console.warn(
      `[generateBath] Sanitarios superpuestos en planta:`,
      collisions.map(([a, b]) => `${a} ↔ ${b}`).join(', '),
    );
  }
  return collisions;
}
